import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../ui/Card';
import { Button } from '../ui/Button';
import { FormLoadingState } from '../ui/FormLoadingState';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface BaseAccountFormProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting?: boolean;
  submitText?: string;
  submitDisabled?: boolean;
  error?: string | null;
  success?: string | null;
  onClearError?: () => void;
  onClearSuccess?: () => void;
  className?: string;
  footerContent?: React.ReactNode;
}

/**
 * Base form component for account management operations
 * Provides consistent layout, error handling, and loading states
 */
export const BaseAccountForm: React.FC<BaseAccountFormProps> = ({
  title,
  description,
  icon,
  children,
  onSubmit,
  isSubmitting = false,
  submitText = 'Save Changes',
  submitDisabled = false,
  error,
  success,
  onClearError,
  onClearSuccess,
  className,
  footerContent
}) => {
  return (
    <Card className={cn('w-full', className)}>
      <CardHeader className="pb-3 sm:pb-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          {icon}
          {title}
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          {description}
        </CardDescription>
      </CardHeader>

      <form onSubmit={onSubmit}>
        <CardContent className="space-y-3 sm:space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm text-red-600 dark:text-red-400 flex-1 leading-relaxed">
                {error}
              </span>
              {onClearError && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearError}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 h-auto p-1 mobile-touch-target"
                >
                  ×
                </Button>
              )}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-md">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs sm:text-sm text-green-600 dark:text-green-400 flex-1 leading-relaxed">
                {success}
              </span>
              {onClearSuccess && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearSuccess}
                  className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 h-auto p-1 mobile-touch-target"
                >
                  ×
                </Button>
              )}
            </div>
          )}

          {/* Form Fields */}
          <FormLoadingState 
            isLoading={isSubmitting}
            loadingText="Saving changes..."
          >
            {children}
          </FormLoadingState>
        </CardContent>

        <CardFooter className="flex flex-col gap-3 pt-3 sm:pt-6">
          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitDisabled || isSubmitting}
            className="w-full mobile-touch-target text-sm sm:text-base"
          >
            {submitText}
          </Button>

          {/* Additional Footer Content */}
          {footerContent}
        </CardFooter>
      </form>
    </Card>
  );
};